/**
 * pushNotifications — small wrapper around the PushManager + our
 * `/api/push/{vapid-public-key,subscribe,unsubscribe,test}` endpoints.
 */
import { authFetch } from './authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getPushPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
};

export const isSubscribed = async () => {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
};

export const subscribeToPush = async () => {
  if (!pushSupported()) {
    return { ok: false, reason: 'unsupported' };
  }
  // 1. Permission.
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: perm };

  // 2. Service worker.
  const reg = await navigator.serviceWorker.ready;

  // 3. Public key.
  const keyRes = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
  if (!keyRes.ok) return { ok: false, reason: 'no-vapid-key' };
  const { public_key } = await keyRes.json();
  if (!public_key) return { ok: false, reason: 'no-vapid-key' };

  // 4. Subscribe (or reuse existing subscription).
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }

  // 5. Send to backend.
  const subJson = sub.toJSON();
  const r = await authFetch(`${BACKEND_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subJson),
  });
  if (!r.ok) return { ok: false, reason: 'backend' };
  return { ok: true };
};

export const unsubscribeFromPush = async () => {
  if (!pushSupported()) return { ok: false };
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  await authFetch(`${BACKEND_URL}/api/push/unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });
  await sub.unsubscribe();
  return { ok: true };
};

export const sendTestPush = async () => {
  const r = await authFetch(`${BACKEND_URL}/api/push/test`, { method: 'POST' });
  return r.ok ? r.json() : { sent: 0, failed: 1 };
};
